import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowLeftIcon, ArrowRightIcon } from '@persona-id/design-tokens/icons/24px';
import { useState } from 'react';
import styled from 'styled-components';

import { ModalBody, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { blueprintIconsMap, BlueprintServerMetadata } from 'dashboard/models/blueprint';
import {
  ModalV2Footer,
  ModalV2Header,
  ModalV2Subheader,
} from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal/BlueprintsQuizV2ModalLayout';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface BlueprintsQuizV2ResultsProps {
  quizResultBlueprintKey: string;
  blueprintsCatalogServer: BlueprintServerMetadata[];
  previouslyUsedBlueprintKeys: string[];
  toPreviousScreen: () => void;
  toBlueprintPreview: (blueprintKey: string) => void;
  createBlueprint: (blueprintKey: string) => Promise<void>;
  closeModal: () => void;
  closeModalOnCompletion: () => void;
  navigateToBlueprintsAllModal: () => void;
}

export const BlueprintsQuizV2Results = ({
  quizResultBlueprintKey,
  blueprintsCatalogServer,
  previouslyUsedBlueprintKeys,
  toPreviousScreen,
  toBlueprintPreview,
  createBlueprint,
  closeModal,
  closeModalOnCompletion,
  navigateToBlueprintsAllModal,
}: BlueprintsQuizV2ResultsProps) => {
  const recommendedBlueprintServerMetadata = blueprintsCatalogServer.find(
    (serverMetadata) => serverMetadata.key === quizResultBlueprintKey,
  );

  const [isCreatingBlueprint, setIsCreatingBlueprint] = useState(false);

  const handleNextScreen = async () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (!previouslyUsedBlueprintKeys.includes(recommendedBlueprintServerMetadata['key'])) {
      setIsCreatingBlueprint(true);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      createBlueprint(recommendedBlueprintServerMetadata['key']).then(() => {
        setIsCreatingBlueprint(false);
        closeModalOnCompletion();
      });
    } else {
      closeModalOnCompletion();
    }
  };

  // blueprintsCatalogServer can be empty if network request fails or user has insufficient permissions
  if (!blueprintsCatalogServer || blueprintsCatalogServer.length === 0) {
    return (
      <>
        <ModalHeader onClose={closeModal}>Your recommendations</ModalHeader>
        <ModalBody>
          <Paragraph color='primary'>
            Sorry, something went wrong. Please refresh the page and try again.
          </Paragraph>
        </ModalBody>
      </>
    );
  }

  return (
    <>
      <ModalHeader onClose={closeModal}>
        <ModalV2Subheader>Step 3 of 3</ModalV2Subheader>
        <ModalV2Header>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          We recommend the {recommendedBlueprintServerMetadata['display-name']} solution
        </ModalV2Header>
      </ModalHeader>
      <ModalBody>
        <BlueprintCard>
          <Card.Body>
            <BlueprintCardHeader>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {recommendedBlueprintServerMetadata['display-name']}
              <Button
                id='blueprints-quiz-v2-results__preview'
                data-test='blueprints-quiz-v2-results__preview'
                aria-label='blueprints-quiz-v2-results__preview'
                width='auto'
                variant='basic'
                type='button'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onClick={() => toBlueprintPreview(recommendedBlueprintServerMetadata.key)}
              >
                Preview solution
              </Button>
            </BlueprintCardHeader>
            <Spacer height={1} />
            <Paragraph color='secondary'>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {recommendedBlueprintServerMetadata.description}
            </Paragraph>
            <Spacer height={1} />
            <BlueprintIconWrapper>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {recommendedBlueprintServerMetadata['display-icon-keys'].map((iconKey, index) => {
                const IconComponent = blueprintIconsMap[iconKey];
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                if (index === recommendedBlueprintServerMetadata['display-icon-keys'].length - 1) {
                  return (
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    <div key={`blueprint-${recommendedBlueprintServerMetadata.key}-icon-${index}`}>
                      <IconComponent />
                    </div>
                  );
                } else {
                  return (
                    <>
                      <div
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        key={`blueprint-${recommendedBlueprintServerMetadata.key}-icon-${index}`}
                      >
                        <IconComponent />
                      </div>
                      <div>+</div>
                    </>
                  );
                }
              })}
            </BlueprintIconWrapper>
          </Card.Body>
        </BlueprintCard>
        <Paragraph color='primary'>
          Based on your needs, we think this will be a good starting point. Next, we’ll show you how
          to customize and test out your solution in your Dashboard. If you’d like to start with
          something different, feel free to{' '}
          <ExploreAllSolutionsLink onClick={() => navigateToBlueprintsAllModal()}>
            explore all of our solutions
          </ExploreAllSolutionsLink>{' '}
          instead.
        </Paragraph>
      </ModalBody>
      <ModalV2Footer>
        <Button
          aria-label='blueprints-quiz-v2-results__button-back'
          width='auto'
          variant='basic'
          type='button'
          onClick={toPreviousScreen}
          prefixIcon={<ArrowLeftIcon />}
        >
          Back
        </Button>
        <Button
          aria-label='blueprints-quiz-v2-results__button-next'
          width='auto'
          type='button'
          onClick={handleNextScreen}
          isPending={isCreatingBlueprint}
          suffixIcon={<ArrowRightIcon />}
        >
          Try this solution in Dashboard
        </Button>
      </ModalV2Footer>
    </>
  );
};

const BlueprintCard = styled(Card)`
  border: 1px solid ${Color.BorderDivider};
`;

const BlueprintCardHeader = styled.div`
  ${Typography.HeadingSmall};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const BlueprintIconWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
`;

const ExploreAllSolutionsLink = styled('span')`
  text-decoration: underline;
  cursor: pointer;

  :hover {
    text-decoration: none;
  }
`;
